import React, { useEffect, useState, useCallback } from "react";
import { api, fmtDate, fmtDateOnly, formatError } from "@/lib/api";
import { SectionTitle, Badge, EmptyState, Btn } from "@/components/Brutal";
import { ExportButtons } from "@/components/ExportButtons";
import { Undo2, Filter, ScanLine, Search, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Returns() {
  const { hasRole } = useAuth();
  const canReturn = hasRole("almoxarife");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collaborators, setCollaborators] = useState([]);
  const [filters, setFilters] = useState({ start: "", end: "", kind: "", collaborator_id: "" });

  // Devolução rápida por CA / código / nome
  const [quickTerm, setQuickTerm] = useState("");
  const [quickResults, setQuickResults] = useState([]);
  const [quickSearching, setQuickSearching] = useState(false);
  const [returning, setReturning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.append(k, v); });
    const [r, c] = await Promise.all([
      api.get(`/reports/returns${qs.toString() ? "?" + qs.toString() : ""}`),
      collaborators.length ? Promise.resolve({ data: collaborators }) : api.get("/collaborators"),
    ]);
    setItems(r.data);
    if (!collaborators.length) setCollaborators(c.data);
    setLoading(false);
  }, [filters, collaborators]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const apply = () => load();

  const quickSearch = async (e) => {
    if (e) e.preventDefault();
    const term = quickTerm.trim();
    if (!term) { setQuickResults([]); return; }
    setQuickSearching(true);
    try {
      const { data } = await api.get(`/epi-deliveries/search-active`, { params: { q: term } });
      setQuickResults(data);
      if (data.length === 0) toast.error("Nenhuma entrega ativa encontrada para: " + term);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setQuickSearching(false);
    }
  };

  const doQuickReturn = async (delivery) => {
    if (!confirm(`Confirmar devolução do EPI ${delivery.product_nome} entregue a ${delivery.collaborator_nome}?\nCA: ${delivery.ca_individual || delivery.product_ca || "—"}`)) return;
    setReturning(delivery.id);
    try {
      await api.post(`/epi-deliveries/${delivery.id}/return`);
      toast.success("Devolução registrada · estoque restaurado");
      // remove das quick results e reload histórico
      setQuickResults((prev) => prev.filter((r) => r.id !== delivery.id));
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setReturning(null);
    }
  };

  const clearQuick = () => { setQuickTerm(""); setQuickResults([]); };

  // Aggregate stats
  const total = items.length;
  const totalEpi = items.filter((d) => d.tipo === "EPI").length;
  const totalReq = items.filter((d) => d.tipo === "Requisição").length;
  const totalQtd = items.reduce((s, d) => s + (Number(d.quantidade) || 0), 0);

  return (
    <div data-testid="returns-page">
      <SectionTitle action={<ExportButtons report="returns" params={filters} />}>
        Devoluções · <Undo2 className="inline w-5 h-5 -mt-1" />
      </SectionTitle>

      {/* DEVOLUÇÃO RÁPIDA por CA / código / nome */}
      {canReturn && (
        <div className="brutal-card p-4 mb-4 bg-[#E8F0EA] border-2 border-[#3D6B57]" data-testid="quick-return-block">
          <div className="flex items-center gap-2 mb-3">
            <ScanLine className="w-5 h-5 text-[#3D6B57]" />
            <span className="font-display font-bold uppercase text-sm tracking-wider text-[#3D6B57]">Devolução Rápida · Bipar CA</span>
          </div>
          <form onSubmit={quickSearch} className="flex flex-wrap gap-2 mb-3">
            <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-white border-2 border-[#111] p-2">
              <Search className="w-4 h-4 text-[#555]" />
              <input
                autoFocus
                value={quickTerm}
                onChange={(e) => setQuickTerm(e.target.value)}
                placeholder='Digite o CA (ex.: "CA 12345"), código, produto ou nome...'
                className="flex-1 outline-none text-sm font-mono-data bg-transparent"
                data-testid="quick-return-input"
              />
              {quickTerm && (
                <button type="button" onClick={clearQuick} className="text-[#777] hover:text-[#E60000]"><X className="w-4 h-4" /></button>
              )}
            </div>
            <Btn type="submit" data-testid="quick-return-search">{quickSearching ? "Buscando..." : "Buscar"}</Btn>
          </form>

          {quickResults.length > 0 && (
            <div className="grid gap-2" data-testid="quick-return-results">
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57]">
                {quickResults.length} entrega(s) ativa(s) encontrada(s) — clique em devolver para dar baixa
              </div>
              {quickResults.map((d) => {
                const ca = d.ca_individual || d.product_ca || "—";
                return (
                  <div key={d.id} className="brutal-card-sm bg-white p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`quick-result-${d.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold uppercase text-sm tracking-tight">{d.product_nome}</div>
                      <div className="font-mono-data text-xs text-[#555]">
                        Recebedor: <b className="text-[#111]">{d.collaborator_nome}</b>
                        {d.sector_nome && <span className="ml-2">· {d.sector_nome}</span>}
                      </div>
                      <div className="font-mono-data text-xs text-[#555] mt-0.5">
                        Código: <b className="text-[#111]">{d.product_codigo}</b>
                        <span className="ml-3">CA: <b className="text-[#111]">{ca}</b></span>
                        <span className="ml-3">Qtd: <b className="text-[#111]">{d.quantidade}</b></span>
                      </div>
                    </div>
                    <button
                      onClick={() => doQuickReturn(d)}
                      disabled={returning === d.id}
                      data-testid={`quick-return-btn-${d.id}`}
                      className="brutal-btn bg-[#1A7F37] text-white px-4 py-2 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                      {returning === d.id ? "Devolvendo..." : "Devolver"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="brutal-card p-4 mb-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-[#111]">
          <Filter className="w-4 h-4" />
          <span className="font-display font-bold uppercase text-sm tracking-wider">Filtros</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">De</label>
            <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="w-full border-2 border-[#111] p-2 text-sm bg-white" data-testid="filter-start" />
          </div>
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Até</label>
            <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="w-full border-2 border-[#111] p-2 text-sm bg-white" data-testid="filter-end" />
          </div>
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Tipo</label>
            <select value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })} className="w-full border-2 border-[#111] p-2 text-sm bg-white" data-testid="filter-kind">
              <option value="">Todos</option>
              <option value="epi">Apenas EPIs</option>
              <option value="requisicao">Apenas Requisições</option>
            </select>
          </div>
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Colaborador (EPI)</label>
            <select value={filters.collaborator_id} onChange={(e) => setFilters({ ...filters, collaborator_id: e.target.value })} className="w-full border-2 border-[#111] p-2 text-sm bg-white" data-testid="filter-collab">
              <option value="">Todos</option>
              {collaborators.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <button onClick={apply} className="brutal-btn bg-[#FF4F00] text-white px-4 py-2 font-display font-bold uppercase text-xs tracking-widest" data-testid="filter-apply">Aplicar</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#555]">Total Devoluções</div><div className="font-display font-black text-2xl">{total}</div></div>
        <div className="brutal-card-sm p-3 bg-[#E8F0EA]"><div className="font-mono-data text-[10px] uppercase text-[#0F2D1F]">EPIs Devolvidos</div><div className="font-display font-black text-2xl">{totalEpi}</div></div>
        <div className="brutal-card-sm p-3 bg-[#FFE9D9]"><div className="font-mono-data text-[10px] uppercase text-[#7C2D12]">Requisições</div><div className="font-display font-black text-2xl">{totalReq}</div></div>
        <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#555]">Qtd Total Itens</div><div className="font-mono-data font-bold text-lg">{totalQtd}</div></div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Tipo</th><th>Nº</th><th>Data Devolução</th><th>Pessoa</th><th>Setor</th><th>Produto</th><th>CA</th><th className="text-right">Qtd</th><th>Responsável</th><th>Observações</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && items.length === 0 && <EmptyState message="Nenhuma devolução no período." cols={10} />}
            {items.map((d) => (
              <tr key={`${d.tipo}-${d.id}`}>
                <td><Badge variant={d.tipo === "EPI" ? "green" : "orange"}>{d.tipo}</Badge></td>
                <td className="font-mono-data font-bold">{d.numero}</td>
                <td className="font-mono-data text-xs">{d.tipo === "EPI" ? fmtDate(d.data) : fmtDate(d.data)}</td>
                <td className="font-medium">{d.pessoa || "—"}</td>
                <td className="text-sm text-[#555]">{d.setor || "—"}</td>
                <td className="text-sm">{d.produto || "—"}</td>
                <td className="font-mono-data text-xs">{d.ca || "—"}</td>
                <td className="text-right font-mono-data font-bold">{d.quantidade}</td>
                <td className="text-xs text-[#555]">{d.responsavel || "—"}</td>
                <td className="text-xs text-[#555] max-w-md truncate" title={d.observacoes}>{d.observacoes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
